import { Button } from 'app/components/ui/button'
import { RadioGroup } from 'app/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import { Field } from 'app/components/workflows/inspectors/fields'
import { patientDetails, patientName, type PatientChoice } from 'app/lib/forms/paperworkRecord'
import { useSendPaperwork } from './useSendPaperwork'

/** A patient the practice marked inactive is rarely the one filling in new paperwork. */
const activeFirst = (a: PatientChoice, b: PatientChoice) =>
  Number(b.status === `ACTIVE`) - Number(a.status === `ACTIVE`)

type Props = {
  patients: PatientChoice[]
  onClose: () => void
}

export function SendPaperworkDialog({ patients, onClose }: Props) {
  const { packets, loadingPackets, packetId, patientId, setPacketId, setPatientId, sending, send } = useSendPaperwork(
    patients,
    onClose,
  )

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-1 px-6 pt-6">
        <h2 className="text-lg font-semibold text-strong">Send paperwork</h2>
        <p className="text-sm text-subtle">
          This makes a private link and copies it. Send it to the patient by text or email.
        </p>
      </div>

      <div className="flex flex-col gap-5 px-6 py-5">
        <Field label="Packet">
          {loadingPackets ? (
            <p className="text-sm text-subtle">Loading packets…</p>
          ) : packets.length > 0 ? (
            <Select value={packetId ?? ``} onValueChange={setPacketId}>
              <SelectTrigger aria-label="Packet">
                <SelectValue placeholder="Choose a packet" />
              </SelectTrigger>
              <SelectContent>
                {packets.map((packet) => (
                  <SelectItem key={packet.id} value={packet.id}>
                    {packet.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className="text-sm text-subtle">Build a packet under Settings, Forms, Packets first.</p>
          )}
        </Field>

        {patients.length > 1 && (
          <Field
            label="Which patient is this for?"
            hint="Finished forms go into this patient's chart, so choose the person who will fill them in."
          >
            <RadioGroup
              name="paperwork-patient"
              orientation="vertical"
              value={patientId}
              onChange={setPatientId}
              values={[...patients].sort(activeFirst).map((patient) => ({
                value: patient.id,
                label: patientName(patient),
                description: patientDetails(patient),
              }))}
            />
          </Field>
        )}
      </div>

      <div className="flex justify-end gap-2 border-t border-default bg-surface-raised px-6 py-4">
        <Button variant="white" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" disabled={!packetId || !patientId || sending} onClick={send}>
          Create and copy link
        </Button>
      </div>
    </div>
  )
}
